/**
 * FaceAuthEnroll.tsx
 * Create ABHA via Face Authentication (QR-based).
 *
 * Flow (matches HealthProPluse):
 *   1. Enter Aadhaar (12 digits) + Mobile (10 digits) + consent (3 items + beneficiary)
 *   2. Init face auth -> generate QR -> poll capturePID
 *   3. Hospital tablet scans QR -> ABHA app captures face via RD Service
 *   4. Poll completes -> enroll by face auth -> ABHA profile created
 *   5. Navigate to success (SuccessView)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAbdm } from '../context/AbdmContext';
import {
    handleFaceAuthInit,
    handleFaceAuthPoll,
    handleFaceAuthEnroll,
    handleViewProfile,
} from '../../utils/api';
import { extractUserProfile } from '../../utils/helpers';

type Phase = 'input' | 'qr' | 'enrolling' | 'success';

const POLL_INTERVAL_MS = 6000;
const QR_VALIDITY_MS = 10 * 60 * 1000; // 10 minutes

const CONSENT_ITEMS = [
    { id: 'aadhaar', label: 'I authorize use of my Aadhaar for ABHA creation as per ABDM guidelines.' },
    { id: 'sharing', label: 'I consent to sharing demographic data with the healthcare provider.' },
    { id: 'healthWorker', label: 'This enrollment is being done by an authorized health worker.' },
] as const;

type ConsentKey = typeof CONSENT_ITEMS[number]['id'];

const PHASE_STEPS = [
    { key: 'input', label: 'DETAILS' },
    { key: 'qr', label: 'QR SCAN' },
    { key: 'enrolling', label: 'ENROLLING' },
    { key: 'success', label: 'SUCCESS' },
] as const;

const STATUS_CONFIG = {
    pending: { color: '#f59e0b', border: '#fde68a', label: 'PENDING', emoji: '⏳' },
    verified: { color: '#3b82f6', border: '#93c5fd', label: 'VERIFYING', emoji: '🔍' },
    complete: { color: '#10b981', border: '#6ee7b7', label: 'COMPLETE', emoji: '✅' },
    failed: { color: '#ef4444', border: '#fca5a5', label: 'FAILED', emoji: '❌' },
    expired: { color: '#94a3b8', border: '#e2e8f0', label: 'EXPIRED', emoji: '⏰' },
} as const;

export const FaceAuthEnroll: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { authData } = state;

    const [phase, setPhase] = useState<Phase>('input');

    // Input fields
    const [aadhaar, setAadhaar] = useState('');
    const [mobile, setMobile] = useState('');
    const [showAadhaar, setShowAadhaar] = useState(false);
    const [beneficiaryName, setBeneficiaryName] = useState('');
    const [beneficiaryConsent, setBeneficiaryConsent] = useState(false);
    const [consents, setConsents] = useState<Record<ConsentKey, boolean>>({ aadhaar: false, sharing: false, healthWorker: false });

    // QR phase
    const [txnId, setTxnId] = useState('');
    const [qrUrl, setQrUrl] = useState('');
    const [qrStatus, setQrStatus] = useState<'pending' | 'verified' | 'complete' | 'failed' | 'expired'>('pending');
    const [qrMessage, setQrMessage] = useState('Waiting for face scan...');
    const [timeLeft, setTimeLeft] = useState(QR_VALIDITY_MS);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const latestTxnId = useRef('');
    const latestAadhaar = useRef('');
    const latestMobile = useRef('');

    // Shared
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalError, setModalError] = useState('');
    const [profileResult, setProfileResult] = useState<any>(null);

    // Validation
    const allConsentsChecked = Object.values(consents).every(Boolean);
    const allConsentsAgreed = allConsentsChecked && beneficiaryConsent && beneficiaryName.trim().length > 0;
    const aadhaarClean = aadhaar.replace(/\s/g, '');
    const aadhaarValid = aadhaarClean.length === 12;
    const mobileValid = /^\d{10}$/.test(mobile);
    const isValid = aadhaarValid && mobileValid && allConsentsAgreed;

    // Keep refs in sync with state
    useEffect(() => { latestAadhaar.current = aadhaar.replace(/\s/g, ''); }, [aadhaar]);
    useEffect(() => { latestMobile.current = mobile; }, [mobile]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }, []);

    const toggleAll = () => {
        const next = !allConsentsChecked;
        const updated: Record<string, boolean> = {};
        CONSENT_ITEMS.forEach(item => { updated[item.id] = next; });
        setConsents(updated as Record<ConsentKey, boolean>);
        setBeneficiaryConsent(next);
    };

    // Get current phase index for step indicator
    const currentPhaseIdx = PHASE_STEPS.findIndex(s => s.key === phase);

    const formatTime = (ms: number) => {
        const totalSec = Math.max(0, Math.floor(ms / 1000));
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
    };

    // ── Phase 1: Init face auth ─────────────────────────────────────────

    const handleInit = async () => {
        if (!authData) { setError('Please login first.'); return; }
        setLoading(true);
        setError('');
        try {
            const result = await handleFaceAuthInit(authData, authData.tenantId);
            setTxnId(result.txnId);
            latestTxnId.current = result.txnId;
            setQrUrl(result.qrUrl);
            setPhase('qr');
            setQrStatus('pending');
            setQrMessage('Waiting for face scan...');
            setTimeLeft(QR_VALIDITY_MS);
            dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            dispatch({ type: 'SET_FACE_AUTH_STATUS', payload: 'pending' });
            dispatch({ type: 'SET_FACE_AUTH_QR_URL', payload: result.qrUrl });

            // Start polling + countdown
            startPolling(result.txnId);
        } catch (err) {
            const msg = (err as Error).message || 'Failed to initialize face auth.';
            if (msg.toLowerCase().includes('abdm') || msg.toLowerCase().includes('uidai')) {
                setModalError(msg);
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Phase 2: Poll for face scan completion ──────────────────────────

    const startPolling = useCallback((pollTxnId: string) => {
        stopPolling();

        const startTime = Date.now();

        // Countdown timer
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = QR_VALIDITY_MS - elapsed;
            if (remaining <= 0) {
                setQrStatus('expired');
                setQrMessage('QR code has expired. Please regenerate.');
                stopPolling();
            } else {
                setTimeLeft(remaining);
            }
        }, 1000);

        // Poll capturePID
        const doPoll = async () => {
            try {
                if (!authData) return;
                const result = await handleFaceAuthPoll(authData, authData.tenantId, latestTxnId.current);
                const s = (result.status || '').toUpperCase();

                if (s === 'COMPLETE' || s === 'SUCCESS') {
                    setQrStatus('complete');
                    setQrMessage('Face verification complete!');
                    stopPolling();
                    handleQrComplete(result.txnId || latestTxnId.current);
                } else if (s === 'VERIFIED') {
                    setQrStatus('verified');
                    setQrMessage('Face captured. Verifying...');
                } else if (s === 'FAILED') {
                    setQrStatus('failed');
                    setQrMessage(result.message || 'Face verification failed.');
                    stopPolling();
                    setModalError(result.message || 'Face verification failed.');
                }
                // else PENDING — keep polling
            } catch {
                // Network error — keep polling (transient)
            }
        };

        // Initial poll after 3s
        const initialTimeout = setTimeout(doPoll, 3000);
        pollRef.current = setInterval(doPoll, POLL_INTERVAL_MS);

        // Store cleanup for initial timeout
        const origCleanup = pollRef.current;
        return () => {
            clearTimeout(initialTimeout);
            if (origCleanup) clearInterval(origCleanup);
        };
    }, [authData, stopPolling]);

    // ── Phase 3: Enroll after QR complete ───────────────────────────────

    const handleQrComplete = async (completedTxnId: string) => {
        if (!authData) return;
        setPhase('enrolling');
        dispatch({ type: 'SET_FACE_AUTH_STATUS', payload: 'complete' });
        setError('');

        try {
            const enrollAadhaar = latestAadhaar.current;
            const enrollMobile = latestMobile.current;
            console.log('[FaceAuthEnroll] Enrolling with:', { txnId: completedTxnId, aadhaar: enrollAadhaar, mobile: enrollMobile, tenantId: authData.tenantId });

            const result = await handleFaceAuthEnroll(
                authData, authData.tenantId, completedTxnId,
                enrollAadhaar, enrollMobile
            );

            console.log('[FaceAuthEnroll] Raw enrollment response:', JSON.stringify(result, null, 2));

            // Flatten nested data
            const flat = result?.data || result || {};

            // Extract xToken from multiple possible locations
            const xToken = flat.xToken || flat.token || flat.tokens?.xToken || flat.tokens?.token
                || result?.xToken || result?.token || result?.tokens?.xToken || result?.tokens?.token || '';

            if (xToken) {
                dispatch({ type: 'SET_X_TOKEN', payload: xToken });
            }

            const txn = flat.txnId || result?.txnId;
            if (txn) {
                dispatch({ type: 'SET_TRANSACTION_ID', payload: txn });
            }

            // Try to get full profile via profile API (most reliable)
            let profile = null;
            if (xToken) {
                try {
                    const abhaNum = flat.ABHANumber || flat.abhaNumber || flat.healthIdNumber || flat.healthId || '';
                    profile = await handleViewProfile(authData, xToken, state.authConfig, abhaNum);
                } catch (e) {
                    console.warn('[FaceAuthEnroll] handleViewProfile failed:', e);
                }
            }

            // Fallback: try extractUserProfile on the raw result
            if (!profile) {
                profile = extractUserProfile(result);
            }

            // Last resort: build minimal profile from whatever fields we can find
            if (!profile) {
                const abhaNum = flat.ABHANumber || flat.abhaNumber || flat.healthIdNumber || flat.healthId
                    || flat.enrolProfile?.ABHANumber || '';
                const abhaAddr = flat.abhaAddress || flat.preferredAbhaAddress || flat.phrAddress
                    || flat.enrolProfile?.abhaAddress || '';
                const name = flat.name || flat.fullName || flat.firstName
                    || flat.enrolProfile?.name || '';

                if (abhaNum || abhaAddr) {
                    profile = {
                        fullName: name || 'Patient',
                        abhaNumber: abhaNum,
                        abhaAddress: abhaAddr,
                        status: 'ACTIVE',
                        kycStatus: 'VERIFIED',
                        mobileVerified: true,
                        mobile: flat.mobile || mobile || '',
                        address: flat.address || '',
                        gender: flat.gender || '',
                        dob: flat.dob || flat.yearOfBirth || '',
                        district: flat.districtName || flat.district || '',
                        state: flat.stateName || flat.state || '',
                    };
                }
            }

            if (profile) {
                dispatch({ type: 'SET_USER_PROFILE', payload: profile });
                dispatch({ type: 'SET_STEP', payload: 'success' });
            } else {
                console.error('[FaceAuthEnroll] Could not extract any profile from enrollment response');
                setError('Enrollment succeeded but profile data could not be retrieved.');
                setProfileResult(result);
                setPhase('success');
            }
        } catch (err) {
            console.error('[FaceAuthEnroll] Enrollment error:', err);
            const msg = (err as Error).message || 'Enrollment failed.';
            setModalError(msg);
            setPhase('qr');
        }
    };

    // ── Navigation ──────────────────────────────────────────────────────

    const handleRegenerate = async () => {
        setError('');
        stopPolling();
        dispatch({ type: 'SET_FACE_AUTH_STATUS', payload: 'idle' });
        await handleInit();
    };

    const goBack = () => {
        stopPolling();
        dispatch({ type: 'SET_FACE_AUTH_STATUS', payload: 'idle' });
        dispatch({ type: 'SET_STEP', payload: 'authType' });
    };

    if (!authData) return null;

    // ── Step Indicator ──────────────────────────────────────────────────

    const renderStepIndicator = () => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '1.25rem' }}>
            {PHASE_STEPS.map((s, i) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: i < currentPhaseIdx ? '#10b981' : i === currentPhaseIdx ? '#2563eb' : '#e2e8f0',
                        color: i <= currentPhaseIdx ? '#fff' : '#94a3b8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 700,
                    }}>
                        {i < currentPhaseIdx ? '✓' : i + 1}
                    </div>
                    <span style={{
                        fontSize: '0.65rem', fontWeight: 600,
                        color: i === currentPhaseIdx ? '#2563eb' : '#94a3b8',
                        display: i < PHASE_STEPS.length - 1 ? 'inline' : 'inline',
                    }}>
                        {s.label}
                    </span>
                    {i < PHASE_STEPS.length - 1 && (
                        <div style={{ width: 20, height: 1, background: '#e2e8f0' }} />
                    )}
                </div>
            ))}
        </div>
    );

    // ── Error Modal ─────────────────────────────────────────────────────

    const errorModal = modalError ? (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999, padding: '1rem',
        }}>
            <div style={{
                background: '#fff', borderRadius: '12px', maxWidth: '420px', width: '100%',
                padding: '1.5rem', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, color: '#dc2626' }}>Error</h3>
                    <button onClick={() => setModalError('')}
                        style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>
                        ✕
                    </button>
                </div>
                <p style={{ color: '#374151', lineHeight: 1.5, marginBottom: '1.25rem' }}>{modalError}</p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-primary" onClick={() => { setModalError(''); if (phase === 'qr' || phase === 'enrolling') handleRegenerate(); }}>
                        🔄 Retry
                    </button>
                    <button className="btn-secondary" onClick={() => { setModalError(''); goBack(); }}>
                        ← Back
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    // ── INPUT PHASE ─────────────────────────────────────────────────────

    if (phase === 'input') {
        return (
            <>
                {errorModal}
                <div className="step-container">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>🤳</span>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Create ABHA (Face Auth)</h2>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                                Register via QR scan + face verification on hospital tablet
                            </p>
                        </div>
                    </div>

                    {renderStepIndicator()}

                    {error && <div className="error-message">{error}</div>}

                    {/* Aadhaar input */}
                    <div className="form-group">
                        <label htmlFor="faceAadhaar">
                            🪪 Aadhaar Number <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                                id="faceAadhaar"
                                type={showAadhaar ? 'text' : 'password'}
                                inputMode="numeric"
                                maxLength={14}
                                placeholder="Enter 12-digit Aadhaar number"
                                value={aadhaar}
                                onChange={e => setAadhaar(e.target.value.replace(/[^\d\s]/g, '').slice(0, 14))}
                                autoComplete="off"
                                style={{ flex: 1 }}
                            />
                            <button type="button" onClick={() => setShowAadhaar(!showAadhaar)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em', padding: '4px' }}
                                tabIndex={-1}>
                                {showAadhaar ? '🙈' : '👁️'}
                            </button>
                        </div>
                        {aadhaarClean.length > 0 && !aadhaarValid && (
                            <p style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4 }}>
                                Must be exactly 12 digits ({aadhaarClean.length}/12)
                            </p>
                        )}
                        {aadhaarValid && (
                            <p style={{ fontSize: '0.78rem', color: '#10b981', marginTop: 4 }}>
                                ✓ Valid Aadhaar number
                            </p>
                        )}
                    </div>

                    {/* Mobile input */}
                    <div className="form-group">
                        <label htmlFor="faceMobile">
                            📱 Mobile Number <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            id="faceMobile"
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="Enter 10-digit mobile number"
                            value={mobile}
                            onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            autoComplete="off"
                        />
                        {mobile.length > 0 && !mobileValid && (
                            <p style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4 }}>
                                Must be exactly 10 digits ({mobile.length}/10)
                            </p>
                        )}
                        {mobileValid && (
                            <p style={{ fontSize: '0.78rem', color: '#10b981', marginTop: 4 }}>
                                ✓ Valid mobile number
                            </p>
                        )}
                    </div>

                    {/* Consent block */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem', marginTop: '0.5rem' }}>
                        <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.95rem' }}>
                                🛡️ Patient Consent
                            </h4>
                            <button type="button" onClick={toggleAll}
                                style={{
                                    background: 'none', border: '1px solid #cbd5e1', borderRadius: 6,
                                    padding: '2px 10px', fontSize: '0.72rem', cursor: 'pointer',
                                    color: allConsentsChecked ? '#ef4444' : '#2563eb', fontWeight: 600,
                                }}>
                                {allConsentsChecked ? 'Uncheck All' : 'Check All'}
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {CONSENT_ITEMS.map(item => (
                                <label key={item.id} style={{ display: 'flex', alignItems: 'start', gap: 8, cursor: 'pointer', fontSize: '0.82rem', lineHeight: 1.4, color: '#475569' }}>
                                    <input
                                        type="checkbox"
                                        checked={consents[item.id]}
                                        onChange={() => setConsents(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                                        style={{ marginTop: 2 }}
                                    />
                                    <span>{item.label}</span>
                                </label>
                            ))}

                            {/* Beneficiary name consent */}
                            <div style={{ marginTop: 6, padding: 10, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <label style={{ display: 'flex', alignItems: 'start', gap: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#0f172a', fontWeight: 600 }}>
                                    <input
                                        type="checkbox"
                                        checked={beneficiaryConsent}
                                        onChange={e => setBeneficiaryConsent(e.target.checked)}
                                        style={{ marginTop: 2 }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        I,{' '}
                                        <input
                                            type="text"
                                            placeholder="Beneficiary Name (as on Aadhaar)"
                                            value={beneficiaryName}
                                            onChange={e => setBeneficiaryName(e.target.value)}
                                            style={{
                                                border: 'none', borderBottom: '1px solid #cbd5e1',
                                                outline: 'none', padding: '0 4px', width: 220,
                                                fontWeight: 800, color: '#2563eb', background: 'transparent',
                                            }}
                                        />
                                        {' '}hereby give my consent for ABHA creation via face authentication.
                                    </div>
                                </label>
                                <p style={{ margin: '4px 0 0 26px', fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    Please enter the beneficiary's name exactly as it appears on their Aadhaar card.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                        <button className="btn-primary" disabled={!isValid || loading} onClick={handleInit}>
                            {loading ? '⏳ Initializing...' : '📷 Generate QR Code'}
                        </button>
                        <button className="btn-secondary" onClick={goBack}>
                            ← Back
                        </button>
                    </div>
                </div>
            </>
        );
    }

    // ── QR PHASE ────────────────────────────────────────────────────────

    if (phase === 'qr') {
        const cfg = STATUS_CONFIG[qrStatus];
        const isActive = qrStatus === 'pending' || qrStatus === 'verified';
        const isTerminal = qrStatus === 'expired' || qrStatus === 'failed';

        return (
            <>
                {errorModal}
                <div className="step-container">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>📱</span>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Scan QR on Hospital Tablet</h2>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                                Open ABHA app on tablet and scan this code
                            </p>
                        </div>
                    </div>

                    {renderStepIndicator()}

                    {error && <div className="error-message">{error}</div>}

                    {/* QR Code display */}
                    <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                        <div style={{
                            background: '#fff', padding: '20px', borderRadius: '16px',
                            display: 'inline-block',
                            border: `2px solid ${isTerminal ? '#e2e8f0' : cfg.border}`,
                            boxShadow: isActive ? '0 0 20px rgba(37,99,235,0.12), 0 4px 16px rgba(0,0,0,0.06)' : '0 2px 8px rgba(0,0,0,0.1)',
                            opacity: isTerminal ? 0.5 : 1,
                            position: 'relative',
                        }}>
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`}
                                alt="Scan with ABHA App"
                                style={{ width: '220px', height: '220px', display: 'block' }}
                            />

                            {/* Complete overlay */}
                            {qrStatus === 'complete' && (
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(255,255,255,0.88)', borderRadius: 14,
                                }}>
                                    <div style={{
                                        width: 64, height: 64, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #10b981, #059669)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '2rem',
                                        boxShadow: '0 8px 24px rgba(16,185,129,0.35)',
                                    }}>
                                        ✅
                                    </div>
                                </div>
                            )}

                            {/* Failed/Expired overlay */}
                            {isTerminal && (
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(255,255,255,0.75)', borderRadius: 14,
                                }}>
                                    <div style={{
                                        width: 56, height: 56, borderRadius: '50%',
                                        background: qrStatus === 'failed'
                                            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                                            : 'linear-gradient(135deg, #94a3b8, #64748b)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.5rem',
                                    }}>
                                        {qrStatus === 'failed' ? '❌' : '⏰'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Status badge */}
                    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '4px 14px', borderRadius: 20,
                            fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: cfg.color, background: `${cfg.color}15`,
                            border: `1px solid ${cfg.border}`,
                        }}>
                            {cfg.emoji} {cfg.label}
                        </span>
                        <p style={{ fontSize: '0.88rem', fontWeight: 600, color: cfg.color, margin: '8px 0 0' }}>
                            {qrMessage}
                        </p>
                    </div>

                    {/* Countdown timer */}
                    {isActive && (
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <span style={{
                                fontSize: '1.1rem', fontWeight: 700,
                                color: timeLeft < 60000 ? '#ef4444' : '#1e293b',
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                ⏱️ {formatTime(timeLeft)}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: 6 }}>remaining</span>
                        </div>
                    )}

                    {/* Instructions */}
                    {isActive && (
                        <div style={{
                            background: '#f0f9ff', borderRadius: '10px', padding: '14px 16px',
                            border: '1px solid #bae6fd', marginBottom: '1rem',
                        }}>
                            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0369a1', marginBottom: '10px' }}>
                                🏥 Instructions for Hospital Tablet
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { step: '1', text: 'Open ABHA app on hospital tablet' },
                                    { step: '2', text: 'Tap QR icon (top-left corner)' },
                                    { step: '3', text: 'Scan this QR code' },
                                    { step: '4', text: 'Complete face scan via RD Service' },
                                ].map(item => (
                                    <div key={item.step} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem', color: '#334155' }}>
                                        <div style={{
                                            width: 22, height: 22, borderRadius: '50%',
                                            background: '#dbeafe', border: '1px solid #93c5fd',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', flexShrink: 0,
                                        }}>
                                            {item.step}
                                        </div>
                                        <span>{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {isTerminal && (
                            <button className="btn-primary" onClick={handleRegenerate}>
                                🔄 Regenerate QR
                            </button>
                        )}
                        <button className="btn-secondary" onClick={goBack}>
                            ← Cancel
                        </button>
                    </div>
                </div>
            </>
        );
    }

    // ── ENROLLING PHASE ─────────────────────────────────────────────────

    if (phase === 'enrolling') {
        return (
            <>
                {errorModal}
                <div className="step-container" style={{ textAlign: 'center' }}>
                    {renderStepIndicator()}
                    <div style={{ margin: '1.5rem 0' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔄</div>
                        <h2 style={{ margin: '0 0 0.25rem' }}>Creating ABHA...</h2>
                        <p style={{ color: '#64748b', maxWidth: 320, margin: '0 auto' }}>
                            Face verified successfully. Enrolling with ABDM...
                        </p>
                    </div>
                    {error && <div className="error-message" style={{ marginTop: '12px' }}>{error}</div>}
                </div>
            </>
        );
    }

    // ── SUCCESS PHASE (fallback — normally dispatches to SuccessView) ──

    return (
        <>
            {errorModal}
            <div className="step-container">
                {renderStepIndicator()}

                {/* Success card */}
                <div style={{
                    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                    borderRadius: '12px', padding: '1.5rem', textAlign: 'center',
                    border: '1px solid #a7f3d0', marginBottom: '1rem',
                }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
                    <h2 style={{ margin: 0, color: '#065f46' }}>ABHA Created Successfully!</h2>
                    <p style={{ margin: '0.5rem 0 0', color: '#047857', fontSize: '0.88rem' }}>via Face Authentication</p>
                </div>

                {/* Profile card if we have data */}
                {(state.userProfile || profileResult) && (() => {
                    const up = state.userProfile;
                    const pr = profileResult;
                    const displayName = up?.fullName || pr?.name || pr?.fullName || 'Patient';
                    const displayAbha = up?.abhaNumber || pr?.ABHANumber || pr?.healthIdNumber || '';
                    const displayAddress = up?.abhaAddress || pr?.abhaAddress || pr?.preferredAbhaAddress || '';
                    const displayMobile = up?.mobile || pr?.mobile || '';

                    return (
                        <div style={{
                            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
                            padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem',
                        }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: '50%',
                                background: '#16a34a', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem',
                            }}>
                                👤
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#14532d' }}>{displayName}</p>
                                {displayAbha && <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#166534', fontFamily: 'monospace' }}>{displayAbha}</p>}
                                {displayAddress && <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#15803d' }}>{displayAddress}</p>}
                                {displayMobile && <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>📱 {displayMobile}</p>}
                            </div>
                        </div>
                    );
                })()}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button className="btn-primary" onClick={() => dispatch({ type: 'SET_STEP', payload: 'success' })} style={{ flex: 2 }}>
                        ✅ Register Patient
                    </button>
                    <button className="btn-secondary" onClick={() => dispatch({ type: 'RESET_FLOW' })} style={{ flex: 1 }}>
                        🔄 New
                    </button>
                </div>
            </div>
        </>
    );
};
